import { Editor } from "@tiptap/core"
import StarterKit from "@tiptap/starter-kit"
import { Mapping } from "@tiptap/pm/transform"
import Link from "@tiptap/extension-link"
import Image from "@tiptap/extension-image"
import { IframeNode } from "./embed_nodes"
import {
  CommentDecorations,
  setCommentAnchors,
  upsertCommentAnchor,
  removeCommentAnchor,
  commentAnchors,
  setActiveAnchor,
} from "./comment_decorations"

const CONTEXT_CHARS = 40

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content
}

function readJSON(id, fallback) {
  const el = document.getElementById(id)
  if (!el) return fallback
  try {
    return JSON.parse(el.textContent.trim())
  } catch {
    return fallback
  }
}

function initDocumentEditor() {
  const root = document.getElementById("document-editor")
  if (!root || root.dataset.editorInitialized === "true") return

  registerStreamActions()

  const initialContent = readJSON("document-editor-initial-content", "<p></p>")
  const initialAnchors = readJSON("document-comment-anchors", [])
  const autosaveUrl = root.dataset.autosaveUrl || null
  const threadsUrl = root.dataset.commentThreadsUrl
  const editable = root.dataset.editable !== "false"
  const statusEl = document.getElementById("document-editor-save-status")

  // The document version (document_texts.version) the in-memory doc was
  // last in sync with. Comment anchors are pinned to this. `unsavedMapping`
  // accumulates every edit since that version so we can project comments that
  // arrive from other users (anchored to the last saved version) onto the
  // editor's current, possibly-edited document.
  let lastSavedVersion = Number(root.dataset.documentVersion || 0)
  let unsavedMapping = new Mapping()
  let dirty = false

  const setStatus = (message, state = "") => {
    if (!statusEl) return
    statusEl.textContent = message
    statusEl.dataset.state = state
  }

  const persist = () => {
    if (!autosaveUrl) return Promise.resolve()
    dirty = false
    setStatus("Saving…", "saving")
    const payload = {
      document_text: {
        text: JSON.stringify(editor.getJSON()),
        base_version: lastSavedVersion,
      },
      anchors: commentAnchors(editor),
    }
    return saveDocument(autosaveUrl, payload)
      .then((data) => {
        lastSavedVersion = data.version
        unsavedMapping = new Mapping()
        setStatus(`Saved (v${data.version})`, "saved")
        return data
      })
      .catch((error) => {
        if (error.conflict) {
          setStatus("Reloading — a newer version exists", "error")
          window.location.reload()
          return
        }
        setStatus("Save failed", "error")
      })
  }

  const editor = new Editor({
    element: root,
    editable,
    extensions: [
      StarterKit,
      CommentDecorations,
      Image.configure({ inline: true, allowBase64: true }),
      IframeNode,
      Link.configure({
        openOnClick: true,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: { rel: "noopener noreferrer nofollow", target: "_blank" },
      }),
    ],
    content: initialContent,
    editorProps: {
      attributes: { class: "document-editor__content" },
    },
    onBlur: () => { if (dirty) persist() },
  })

  // Keep the running map of edits-since-last-save up to date.
  editor.on("transaction", ({ transaction }) => {
    if (transaction.docChanged) {
      unsavedMapping.appendMapping(transaction.mapping)
      dirty = true
      setStatus("Unsaved changes", "unsaved")
    }
  })

  const ctx = {
    editor,
    threadsUrl,
    autosaveUrl,
    editable,
    persist,
    getVersion: () => lastSavedVersion,
    getMapping: () => unsavedMapping,
  }

  // Paint the comments that already exist on the document.
  const resolved = initialAnchors
    .map((a) => resolveAnchor(ctx, a))
    .filter(Boolean)
  setCommentAnchors(editor, resolved)

  wireToolbar(editor)
  wireCommenting(ctx)

  root.dataset.editorInitialized = "true"
  root._tiptapEditor = editor

  const saveBeforeVisit = () => { if (dirty) persist() }
  document.addEventListener("turbo:before-visit", saveBeforeVisit)

  document.addEventListener(
    "turbo:before-cache",
    () => {
      document.removeEventListener("turbo:before-visit", saveBeforeVisit)
      teardownCommenting()
      editor.destroy()
      delete root.dataset.editorInitialized
      delete root._tiptapEditor
    },
    { once: true }
  )
}

// Rich-text capture for the "Copy-and-Paste from Web" flow on the new-document
// form. It's NOT for editing — it exists so a paste keeps the clipboard's
// text/html flavour (formatting, links, images) instead of the text/plain a
// <textarea> would collapse it to. The editor mirrors its HTML into a hidden
// input on every change; the server converts that HTML with Tiptap.from_html,
// so the extension list here matches script/tiptap_convert.mjs (the server
// schema) to guarantee a clean round-trip.
function initWebPasteEditor() {
  const root = document.getElementById("web-paste-editor")
  if (!root || root.dataset.editorInitialized === "true") return

  const input = document.querySelector('[data-web-paste-input]')
  const sync = (editor) => {
    if (input) input.value = editor.isEmpty ? "" : editor.getHTML()
  }

  const editor = new Editor({
    element: root,
    editable: true,
    extensions: [
      StarterKit,
      Image.configure({ inline: true, allowBase64: true }),
      IframeNode,
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: { rel: "noopener noreferrer nofollow", target: "_blank" },
      }),
    ],
    content: (input && input.value) || "<p></p>",
    editorProps: {
      attributes: { class: "document-editor__content" },
    },
    onUpdate: ({ editor }) => sync(editor),
  })

  // Guarantee the freshest HTML is in the hidden field when the form submits,
  // even if a submit races an in-flight update.
  const form = root.closest("form")
  if (form) form.addEventListener("submit", () => sync(editor))

  // The contenteditable child is only as tall as its content, so clicks in the
  // empty area below it (or the padding) land on the mount, not the editor.
  // Treat those as "focus at the end" so the whole box behaves like one field.
  root.addEventListener("mousedown", (event) => {
    if (event.target === root) {
      event.preventDefault()
      editor.commands.focus("end")
    }
  })

  root.dataset.editorInitialized = "true"
  root._tiptapEditor = editor

  document.addEventListener(
    "turbo:before-cache",
    () => {
      editor.destroy()
      delete root.dataset.editorInitialized
      delete root._tiptapEditor
    },
    { once: true }
  )
}

async function saveDocument(url, payload) {
  const response = await fetch(url, {
    method: "PATCH",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    body: JSON.stringify(payload),
  })

  if (response.status === 409) {
    const error = new Error("conflict")
    error.conflict = true
    throw error
  }
  if (!response.ok) throw new Error("autosave failed")

  return response.json()
}

function wireToolbar(editor) {
  const toolbar = document.getElementById("document-editor-toolbar")
  if (!toolbar) return

  toolbar.addEventListener("click", (event) => {
    const button = event.target.closest("[data-editor-command]")
    if (!button) return
    event.preventDefault()

    const command = button.dataset.editorCommand
    const chain = editor.chain().focus()
    switch (command) {
      case "bold":
        chain.toggleBold().run()
        break
      case "italic":
        chain.toggleItalic().run()
        break
      case "paragraph":
        chain.setParagraph().run()
        break
      case "heading":
        chain.toggleHeading({ level: Number(button.dataset.level) }).run()
        break
      case "bulletList":
        chain.toggleBulletList().run()
        break
      case "orderedList":
        chain.toggleOrderedList().run()
        break
      default:
        break
    }
  })

  // Reflect the current selection in the toolbar (active buttons + the block
  // style dropdown label).
  const linkToggle = toolbar.querySelector("[data-link-toggle]")
  const updateToolbar = () => {
    toolbar.querySelectorAll("[data-editor-command]").forEach((button) => {
      button.classList.toggle("active", isCommandActive(editor, button))
    })
    if (linkToggle) linkToggle.classList.toggle("active", editor.isActive("link"))
    const label = toolbar.querySelector("[data-block-label]")
    if (label) label.textContent = currentBlockLabel(editor)
  }
  editor.on("selectionUpdate", updateToolbar)
  editor.on("transaction", updateToolbar)
  updateToolbar()

  wireLinkEditor(editor)
}

function isCommandActive(editor, button) {
  switch (button.dataset.editorCommand) {
    case "bold":
      return editor.isActive("bold")
    case "italic":
      return editor.isActive("italic")
    case "bulletList":
      return editor.isActive("bulletList")
    case "orderedList":
      return editor.isActive("orderedList")
    case "link":
      return editor.isActive("link")
    case "heading":
      return editor.isActive("heading", { level: Number(button.dataset.level) })
    default:
      return false
  }
}

function currentBlockLabel(editor) {
  for (let level = 1; level <= 4; level++) {
    if (editor.isActive("heading", { level })) return `Heading ${level}`
  }
  return "Plain text"
}

// Wires the link toolbar dropdown: an input plus apply / visit / remove
// buttons, populated from the link under the cursor when opened.
function wireLinkEditor(editor) {
  const dropdown = document.getElementById("link-dropdown")
  if (!dropdown) return
  const toggle = dropdown.querySelector("[data-link-toggle]")
  const input = dropdown.querySelector("[data-link-input]")
  if (!toggle || !input) return

  const close = () => window.bootstrap?.Dropdown?.getOrCreateInstance(toggle)?.hide()

  const apply = () => {
    const url = input.value.trim()
    const chain = editor.chain().focus().extendMarkRange("link")
    if (url === "") chain.unsetLink().run()
    else chain.setLink({ href: normalizeUrl(url) }).run()
    close()
  }

  const remove = () => {
    editor.chain().focus().extendMarkRange("link").unsetLink().run()
    close()
  }

  const visit = () => {
    const url = normalizeUrl(input.value.trim() || editor.getAttributes("link").href || "")
    if (url && url !== "#") window.open(url, "_blank", "noopener,noreferrer")
  }

  // Prefill from the current link (if any) each time the menu opens.
  dropdown.addEventListener("show.bs.dropdown", () => {
    input.value = editor.getAttributes("link").href || ""
  })
  dropdown.addEventListener("shown.bs.dropdown", () => {
    input.focus()
    input.select()
  })

  input.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      event.preventDefault()
      apply()
    }
  })

  dropdown.addEventListener("click", (event) => {
    const action = event.target.closest("[data-link-action]")?.dataset.linkAction
    if (action === "apply") apply()
    else if (action === "remove") remove()
    else if (action === "visit") visit()
  })
}

function normalizeUrl(url) {
  if (/^\s*(javascript|data):/i.test(url)) return "#" // never allow script URLs
  if (/^(https?:|mailto:|tel:)/i.test(url) || url.startsWith("/") || url.startsWith("#")) {
    return url
  }
  return `https://${url}`
}

// --- Commenting -----------------------------------------------------------

let anchorEventHandler = null
let anchorRemoveHandler = null
let clickHandler = null
let dragHandler = null

function teardownClicks() {
  if (clickHandler) {
    document.removeEventListener("click", clickHandler)
    clickHandler = null
  }
}

function teardownDrag() {
  if (dragHandler) {
    document.removeEventListener("mousedown", dragHandler)
    dragHandler = null
  }
}

function teardownCommenting() {
  teardownClicks()
  teardownDrag()
  if (anchorEventHandler) {
    window.removeEventListener("comment:anchor", anchorEventHandler)
    anchorEventHandler = null
  }
  if (anchorRemoveHandler) {
    window.removeEventListener("comment:anchor-remove", anchorRemoveHandler)
    anchorRemoveHandler = null
  }
}

function wireCommenting(ctx) {
  const { editor, threadsUrl } = ctx
  if (!threadsUrl) return

  // Remove handlers left over from a previous initialisation (Turbo nav) *before*
  // wiring up new ones, so cleanup never tears down the handlers we add below.
  teardownCommenting()

  const button = createAddCommentButton()
  let busy = false

  const hideButton = () => {
    button.style.display = "none"
  }

  const positionButton = () => {
    // In read-only mode the comment buttons never appear.
    const panel = document.querySelector(".workspace_panel")
    if (panel && panel.dataset.readonly === "true") return hideButton()

    // Drive visibility off the live DOM selection (not ProseMirror's internal
    // selection, which lingers after the editor loses focus). Hide unless there
    // is a non-collapsed selection inside the editor.
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return hideButton()
    const range = selection.getRangeAt(0)
    if (!editor.view.dom.contains(range.commonAncestorContainer)) return hideButton()
    const rect = range.getBoundingClientRect()
    if (!rect || (rect.width === 0 && rect.height === 0)) return hideButton()

    // Hide when the selection has scrolled out of the editor's visible bounds.
    const scroller = editor.view.dom.closest(".overflow-y-auto")
    if (scroller) {
      const bounds = scroller.getBoundingClientRect()
      if (rect.bottom < bounds.top || rect.top > bounds.bottom) return hideButton()
    }

    button.style.display = "block"
    button.style.top = `${rect.bottom + window.scrollY + 6}px`
    button.style.left = `${rect.left + window.scrollX}px`
  }

  editor.on("selectionUpdate", positionButton)
  // selectionUpdate doesn't fire for plain DOM selection in a read-only editor,
  // so also listen to the document-level selection event for comment-only mode.
  document.addEventListener("selectionchange", positionButton)
  // Reposition (rather than hide) on scroll so the button follows the selection
  // and persists until it's clicked or the text is deselected.
  document.addEventListener("scroll", positionButton, true)

  // While a selection drag is in progress, make the button transparent to the
  // mouse. In Firefox the drag passing over the button otherwise causes the
  // selection to jump across the whole page. We watch at the document level and
  // trigger for drags starting anywhere in the editor area (the content *or* the
  // shell's surrounding padding); the button lives outside the shell so clicks
  // on it are unaffected. Pointer events are restored on release.
  dragHandler = (event) => {
    if (!event.target.closest(".document-editor-shell")) return
    button.style.pointerEvents = "none"
    document.addEventListener(
      "mouseup",
      () => {
        button.style.pointerEvents = ""
        positionButton()
      },
      { once: true }
    )
  }
  document.addEventListener("mousedown", dragHandler)

  button.addEventListener("mousedown", (event) => event.preventDefault())

  button.addEventListener("click", async () => {
    if (busy) return
    const range = selectedRange(editor)
    if (!range) return
    const { from, to } = range
    const context = anchorContext(editor, from, to)

    busy = true
    hideButton()
    try {
      // For the editor (owner) flush pending edits first, so the anchor we send
      // is pinned to a real saved version. Comment-only viewers can't save.
      if (ctx.autosaveUrl) await ctx.persist()
      const baseVersion = ctx.getVersion()
      const thread = await createThread(threadsUrl, {
        from,
        to,
        base_version: baseVersion,
        ...context,
      })
      upsertCommentAnchor(editor, { threadId: thread.id, from, to })
      insertThreadCard(thread.html, thread.id)
      // On mobile the comments panel is a hidden bottom sheet; auto-open it to
      // the freshly created pending comment. No-op on desktop.
      openCommentsSheet(thread.id)
    } catch (error) {
      console.error("Failed to create comment thread", error)
    } finally {
      busy = false
    }
  })

  // Single document-level click handler so that the highlighted selection
  // persists until the *next* click anywhere, which clears it (unless that
  // click lands on another comment, which moves the highlight).
  clickHandler = (event) => {
    // Cancel a draft composer.
    const cancel = event.target.closest("[data-thread-cancel]")
    if (cancel) {
      event.preventDefault()
      cancelThread(editor, cancel.dataset.threadCancel)
      setActiveAnchor(editor, null)
      return
    }

    // Click a comment badge in the document -> focus its card (but don't
    // highlight the selection; only clicking a comment does that).
    const badge = event.target.closest(".comment-badge[data-thread-ids]")
    if (badge) {
      setActiveAnchor(editor, null)
      const id = badge.dataset.threadIds.split(",")[0]
      if (id) {
        // On mobile, open the bottom sheet and scroll to this comment; on
        // desktop openCommentsSheet is a no-op and activateThread handles it.
        openCommentsSheet(id)
        activateThread(id)
      }
      return
    }

    // Click anywhere on a comment card (but not its controls) -> highlight its
    // selection and scroll the document to it.
    const card = event.target.closest(".document-thread-card")
    const onControl = event.target.closest("button, a, input, textarea, select, label, .dropdown-menu")
    if (card && card.dataset.threadId && !onControl) {
      setActiveAnchor(editor, card.dataset.threadId)
      // On mobile, close the sheet so the highlighted selection is visible in
      // the editor. No-op on desktop where the panel is static.
      closeCommentsSheet()
      scrollToActive(editor)
      return
    }

    // Any other click clears the highlight.
    setActiveAnchor(editor, null)
  }
  document.addEventListener("click", clickHandler)

  // Comments published by other users arrive here in real time.
  anchorEventHandler = (event) => {
    const anchor = resolveAnchor(ctx, event.detail)
    if (anchor) upsertCommentAnchor(editor, anchor)
  }
  window.addEventListener("comment:anchor", anchorEventHandler)

  // A deleted thread's highlight is removed here.
  anchorRemoveHandler = (event) => {
    removeCommentAnchor(editor, event.detail.threadId)
  }
  window.addEventListener("comment:anchor-remove", anchorRemoveHandler)
}

// Resolve a stored/broadcast anchor to live positions in the current document.
// Precise path: if the anchor is pinned to our last saved version, map it
// through the edits we've made since. Fallback: locate it by quote + context.
function resolveAnchor(ctx, detail) {
  if (!detail || detail.orphaned) return null
  const threadId = detail.threadId

  if (
    Number.isInteger(detail.from) &&
    Number.isInteger(detail.to) &&
    detail.baseVersion === ctx.getVersion()
  ) {
    const mapping = ctx.getMapping()
    const from = mapping.map(detail.from, -1)
    const to = mapping.map(detail.to, 1)
    if (to > from) return { threadId, from, to }
  }

  const found = placeByQuote(ctx.editor, detail)
  return found ? { threadId, ...found } : null
}

function selectedRange(editor) {
  const selection = window.getSelection()
  if (selection && selection.rangeCount && !selection.isCollapsed) {
    const range = selection.getRangeAt(0)
    if (editor.view.dom.contains(range.commonAncestorContainer)) {
      try {
        let from = editor.view.posAtDOM(range.startContainer, range.startOffset)
        let to = editor.view.posAtDOM(range.endContainer, range.endOffset)
        if (from > to) [from, to] = [to, from]
        if (to > from) return { from, to }
      } catch {
        // fall through to PM selection
      }
    }
  }
  const { from, to } = editor.state.selection
  return to > from ? { from, to } : null
}

function anchorContext(editor, from, to) {
  const start = Math.max(0, from - CONTEXT_CHARS)
  const end = Math.min(editor.state.doc.content.size, to + CONTEXT_CHARS)
  return {
    quote: editor.state.doc.textBetween(from, to, ""),
    prefix: editor.state.doc.textBetween(start, from, ""),
    suffix: editor.state.doc.textBetween(to, end, ""),
  }
}

function buildTextIndex(doc) {
  let text = ""
  const map = []
  doc.descendants((node, pos) => {
    if (node.isText && node.text) {
      for (let i = 0; i < node.text.length; i++) {
        text += node.text[i]
        map.push(pos + i)
      }
    }
    return true
  })
  return { text, map }
}

function placeByQuote(editor, detail) {
  const quote = detail.quote || ""
  if (!quote) return null
  const { text, map } = buildTextIndex(editor.state.doc)
  const prefix = detail.prefix || ""
  const suffix = detail.suffix || ""

  let idx = -1
  if (prefix || suffix) {
    const ci = text.indexOf(prefix + quote + suffix)
    if (ci !== -1) idx = ci + prefix.length
  }
  if (idx === -1) idx = text.indexOf(quote)
  if (idx === -1) return null

  const from = map[idx]
  const lastChar = map[idx + quote.length - 1]
  if (from == null || lastChar == null) return null
  return { from, to: lastChar + 1 }
}

function createAddCommentButton() {
  let button = document.getElementById("comment-add-button")
  if (button) return button
  button = document.createElement("button")
  button.id = "comment-add-button"
  button.type = "button"
  button.className = "comment-add-button btn btn-dark btn-sm"
  button.innerHTML = '<i class="bi bi-chat-left-text"></i> Comment'
  button.style.display = "none"
  document.body.appendChild(button)
  return button
}

async function createThread(url, body) {
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    body: JSON.stringify(body),
  })
  if (!response.ok) throw new Error("thread creation failed")
  return response.json()
}

// Insert a thread card into the sidebar keeping document order: sorted by the
// selection's end position, then start, matching the server-side ordering.
function insertCardSorted(node) {
  const container = document.getElementById("document-threads")
  if (!container) return null
  if (document.getElementById(node.id)) return document.getElementById(node.id)

  const to = Number(node.dataset.anchorTo)
  const from = Number(node.dataset.anchorFrom)

  let ref = null
  for (const card of container.querySelectorAll(".document-thread-card")) {
    const cardTo = Number(card.dataset.anchorTo)
    const cardFrom = Number(card.dataset.anchorFrom)
    if (cardTo > to || (cardTo === to && cardFrom > from)) {
      ref = card
      break
    }
  }

  document.querySelector(".document-threads-empty")?.classList.add("d-none")
  container.insertBefore(node, ref)
  return node
}

function parseCard(html) {
  const tmp = document.createElement("div")
  tmp.innerHTML = html.trim()
  return tmp.firstElementChild
}

function insertThreadCard(html, threadId) {
  let card = document.getElementById(`document_thread_${threadId}`)
  if (!card) card = insertCardSorted(parseCard(html))
  if (!card) return
  card.scrollIntoView({ behavior: "smooth", block: "center" })
  card.querySelector("textarea")?.focus()
}

// Ask the mobile comments-sheet controller to open (and optionally scroll to a
// given thread). Purely a signal — inert on desktop where the panel is static.
function openCommentsSheet(threadId) {
  window.dispatchEvent(
    new CustomEvent("comments-sheet:open", { detail: { threadId } })
  )
}

// Ask the mobile comments-sheet controller to close. Inert on desktop.
function closeCommentsSheet() {
  window.dispatchEvent(new CustomEvent("comments-sheet:close"))
}

function activateThread(threadId) {
  const card = document.getElementById(`document_thread_${threadId}`)
  if (!card) return
  card.scrollIntoView({ behavior: "smooth", block: "center" })
  card.classList.add("is-active")
  setTimeout(() => card.classList.remove("is-active"), 1500)
}

// Inverse of activateThread: scroll the document to a comment's highlight and
// flash it. A range can be split across several decoration spans, so flash them
// all and scroll to the first. Orphaned/unplaced threads have no span -> no-op.
// Scroll the document to the currently-highlighted selection. Driven off the
// decoration the plugin just painted, so it reflects live positions.
function scrollToActive(editor) {
  const span = editor.view.dom.querySelector(".comment-flash")
  if (span) span.scrollIntoView({ behavior: "smooth", block: "center" })
}

async function cancelThread(editor, threadId) {
  removeCommentAnchor(editor, threadId)

  await fetch(`/document_threads/${threadId}`, {
    method: "DELETE",
    headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
  }).catch(() => {})

  document.getElementById(`document_thread_${threadId}`)?.remove()

  const container = document.getElementById("document-threads")
  if (container && container.children.length === 0) {
    document.querySelector(".document-threads-empty")?.classList.remove("d-none")
  }
}

// --- Custom Turbo Stream actions (real-time comment delivery) --------------

function registerStreamActions() {
  const Turbo = window.Turbo
  if (!Turbo || !Turbo.StreamActions || Turbo.StreamActions.__commentActions) return
  Turbo.StreamActions.__commentActions = true

  // Append a freshly-published thread card unless we already have it (the
  // author inserted it locally).
  Turbo.StreamActions["upsert-comment-thread"] = function () {
    const node = this.templateContent.firstElementChild
    if (!node || document.getElementById(node.id)) return
    insertCardSorted(node)
  }

  // Hand a comment anchor to the editor's decoration layer.
  Turbo.StreamActions["comment-anchor"] = function () {
    try {
      const detail = JSON.parse(this.templateContent.textContent)
      window.dispatchEvent(new CustomEvent("comment:anchor", { detail }))
    } catch {
      // ignore malformed payloads
    }
  }

  // Drop a comment's highlight from the editor when its thread is deleted.
  Turbo.StreamActions["remove-comment-anchor"] = function () {
    const threadId = this.getAttribute("thread-id")
    if (threadId) {
      window.dispatchEvent(new CustomEvent("comment:anchor-remove", { detail: { threadId } }))
    }
  }
}

// --- Comments panel resize ------------------------------------------------

function initCommentsResize() {
  const handle = document.getElementById("document-comments-resize-handle")
  const panel = document.getElementById("document-comments-panel")
  const page = handle?.closest(".document-editor-page")
  if (!handle || !panel || !page) return

  let startX, startWidth, pageWidth

  handle.addEventListener("mousedown", (e) => {
    e.preventDefault()
    startX = e.clientX
    startWidth = panel.getBoundingClientRect().width
    pageWidth = page.getBoundingClientRect().width
    handle.classList.add("is-dragging")

    function onMouseMove(e) {
      const delta = startX - e.clientX
      const newWidth = Math.max(Math.min(Math.max(startWidth + delta, 0), pageWidth * 0.60), pageWidth * 0.25)
      panel.style.flex = `0 0 ${newWidth}px`
    }

    function onMouseUp() {
      handle.classList.remove("is-dragging")
      document.removeEventListener("mousemove", onMouseMove)
      document.removeEventListener("mouseup", onMouseUp)
    }

    document.addEventListener("mousemove", onMouseMove)
    document.addEventListener("mouseup", onMouseUp)
  })
}

document.addEventListener("turbo:load", () => { initDocumentEditor(); initCommentsResize(); initWebPasteEditor() })
document.addEventListener("DOMContentLoaded", () => { initDocumentEditor(); initCommentsResize(); initWebPasteEditor() })
